import React, { useState, useCallback } from 'react';
import { Upload, Copy, Loader2, X, Download, Sun, Moon } from 'lucide-react';

const KeywordGenerator = () => {
  const [apiKey, setApiKey] = useState('');
  const [images, setImages] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [language, setLanguage] = useState('russian');
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isProcessingImages, setIsProcessingImages] = useState(false);
  const [processingProgress, setProcessingProgress] = useState({ current: 0, total: 0 });
  const [retryingImages, setRetryingImages] = useState(new Set());

  // Переводы интерфейса
  const translations = {
    russian: {
      title: "🔍 AI Генератор Ключевых Слов",
      apiKeyLabel: "🔑 OpenAI API Ключ",
      apiKeyPlaceholder: "sk-proj-...",
      languageLabel: "🌍 Язык Генерации",
      processingImages: "🔄 Обработка изображений...",
      dropImages: "Перетащите изображения сюда",
      dropImagesProcessing: "Обработка изображений...",
      clickToSelect: "или нажмите для выбора файлов",
      waitOptimizing: "Пожалуйста, подождите, пока мы оптимизируем ваши изображения",
      selectImages: "Выбрать изображения",
      processing: "Обработка...",
      generateKeywords: "✨ Сгенерировать ключевые слова",
      generatingMagic: "Генерируем магию...",
      readyToGenerate: "Готовы генерировать ключевые слова?",
      uploadToStart: "Загрузите изображения для начала работы с ИИ-генерацией ключевых слов",
      features: "⚡ Параллельная обработка • 🎯 Умное сжатие • 🌍 Поддержка многих языков",
      processingYourImages: "Обрабатываем ваши изображения...",
      compressingOptimizing: "Сжимаем и оптимизируем",
      usingParallelProcessing: "🚀 Используем параллельную обработку для лучшей производительности",
      additionalContext: "💡 Дополнительный контекст",
      contextPlaceholder: "Местоположение, событие, настроение...",
      title_field: "📝 Название:",
      keywords_field: "🏷️ Ключевые слова:",
      categories_field: "📂 Категории:",
      editorial: "Editorial",
      mature: "Mature",
      illustration: "Illustration",
      copyData: "Копировать данные",
      retry: "🔄 Повторить",
      retrying: "Повторяем...",
      processingResults: "📊 Результаты обработки",
      copyCSV: "Копировать CSV",
      downloadCSV: "Скачать CSV",
      processed: "Обработано:",
      of: "из",
      images: "изображений",
      aiPoweredGeneration: "✨ ИИ-генерация ключевых слов",
      filename: "Имя файла",
      description: "Описание",
      keywords: "Ключевые слова",
      categories: "Категории"
    },
    english: {
      title: "🔍 AI Keyword Generator",
      apiKeyLabel: "🔑 OpenAI API Key",
      apiKeyPlaceholder: "sk-proj-...",
      languageLabel: "🌍 Generation Language",
      processingImages: "🔄 Processing Images...",
      dropImages: "Drop your images here",
      dropImagesProcessing: "Processing Images...",
      clickToSelect: "or click to select files",
      waitOptimizing: "Please wait while we optimize your images",
      selectImages: "Select Images",
      processing: "Processing...",
      generateKeywords: "✨ Generate Keywords",
      generatingMagic: "Generating Magic...",
      readyToGenerate: "Ready to generate keywords?",
      uploadToStart: "Upload your images to get started with AI-powered keyword generation",
      features: "⚡ Parallel processing • 🎯 Smart compression • 🌍 Multi-language support",
      processingYourImages: "Processing your images...",
      compressingOptimizing: "Compressing and optimizing",
      usingParallelProcessing: "🚀 Using parallel processing for faster performance",
      additionalContext: "💡 Additional Context",
      contextPlaceholder: "Location, event, mood...",
      title_field: "📝 Title:",
      keywords_field: "🏷️ Keywords:",
      categories_field: "📂 Categories:",
      editorial: "Editorial",
      mature: "Mature",
      illustration: "Illustration",
      copyData: "Copy Data",
      retry: "🔄 Retry",
      retrying: "Retrying...",
      processingResults: "📊 Processing Results",
      copyCSV: "Copy CSV",
      downloadCSV: "Download CSV",
      processed: "Processed:",
      of: "of",
      images: "images",
      aiPoweredGeneration: "✨ AI-powered keyword generation",
      filename: "Filename",
      description: "Description",
      keywords: "Keywords",
      categories: "Categories"
    }
  };

  const t = translations[language];

  // Функция сжатия изображения
  const compressImage = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();
        
        img.onload = () => {
          try {
            const MAX_SIZE = 500;
            let { width, height } = img;
            
            // Вычисляем новые размеры
            if (width > height) {
              if (width > MAX_SIZE) {
                height = (height * MAX_SIZE) / width;
                width = MAX_SIZE;
              }
            } else {
              if (height > MAX_SIZE) {
                width = (width * MAX_SIZE) / height;
                height = MAX_SIZE;
              }
            }
            
            canvas.width = width;
            canvas.height = height;
            
            // Рисуем изображение
            ctx.drawImage(img, 0, 0, width, height);
            
            // Получаем data URL
            const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
            
            resolve({
              width,
              height,
              dataUrl
            });
          } catch (error) {
            console.error('Ошибка сжатия:', error);
            reject(error);
          }
        };
        
        img.onerror = () => {
          console.error('Ошибка загрузки изображения');
          reject(new Error('Ошибка загрузки изображения'));
        };
        
        // Используем data URL напрямую
        img.src = e.target.result;
      };
      
      reader.onerror = () => {
        console.error('Ошибка чтения файла');
        reject(new Error('Ошибка чтения файла'));
      };
      
      // Читаем файл как data URL
      reader.readAsDataURL(file);
    });
  };

  // Обработка загрузки файлов с параллельным сжатием
  const handleFiles = async (files) => {
    console.log('Файлы получены:', files.length);
    const fileArray = Array.from(files);
    const imageFiles = fileArray.filter(file => file.type.startsWith('image/'));
    console.log('Изображений найдено:', imageFiles.length);
    
    if (imageFiles.length === 0) {
      alert(language === 'russian' ? 'Пожалуйста, выберите файлы изображений (JPG, PNG, и т.д.)' : 'Please select image files (JPG, PNG, etc.)');
      return;
    }

    setIsProcessingImages(true);
    setProcessingProgress({ current: 0, total: imageFiles.length });

    try {
      // Функция для обработки одного файла с отслеживанием прогресса
      const processFile = async (file, index) => {
        try {
          console.log(`Обрабатываем файл ${index + 1}/${imageFiles.length}:`, file.name);
          const compressed = await compressImage(file);
          
          // Обновляем прогресс
          setProcessingProgress(prev => ({ 
            current: prev.current + 1, 
            total: prev.total 
          }));

          return {
            id: Date.now() + Math.random() + index,
            originalName: file.name,
            dataUrl: compressed.dataUrl,
            additionalInfo: '',
            title: '',
            keywords: '',
            categories: '',
            editorial: 'no',
            matureContent: 'no',
            illustration: 'no',
            isProcessed: false
          };
        } catch (error) {
          console.error('Ошибка обработки файла:', file.name, error);
          
          // Все равно обновляем прогресс
          setProcessingProgress(prev => ({ 
            current: prev.current + 1, 
            total: prev.total 
          }));
          
          return null;
        }
      };

      // Обрабатываем файлы пакетами по 5 штук для избежания перегрузки
      const batchSize = 15;
      const newImages = [];
      
      for (let i = 0; i < imageFiles.length; i += batchSize) {
        const batch = imageFiles.slice(i, i + batchSize);
        const batchPromises = batch.map((file, batchIndex) => 
          processFile(file, i + batchIndex)
        );
        
        const batchResults = await Promise.all(batchPromises);
        const validResults = batchResults.filter(result => result !== null);
        newImages.push(...validResults);
        
        // Небольшая задержка между пакетами для UI responsiveness
        if (i + batchSize < imageFiles.length) {
          await new Promise(resolve => setTimeout(resolve, 50));
        }
      }

      if (newImages.length > 0) {
        setImages(prev => [...prev, ...newImages]);
        console.log('Добавлено изображений:', newImages.length);
      }

    } catch (error) {
      console.error('Ошибка при обработке файлов:', error);
      alert(language === 'russian' ? 'Произошла ошибка при обработке изображений' : 'An error occurred while processing images');
    } finally {
      setIsProcessingImages(false);
      setProcessingProgress({ current: 0, total: 0 });
    }
  };

  // Drag & Drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const files = e.dataTransfer.files;
    handleFiles(files);
  };

  // Обработка выбора файлов через input
  const handleFileInput = (e) => {
    const files = e.target.files;
    handleFiles(files);
    e.target.value = '';
  };

  // Удаление изображения
  const removeImage = (id) => {
    setImages(prev => prev.filter(img => img.id !== id));
  };

  // Обновление дополнительной информации
  const updateAdditionalInfo = (id, info) => {
    setImages(prev => prev.map(img => 
      img.id === id ? { ...img, additionalInfo: info } : img
    ));
  };

  // Генерация ключевых слов через OpenAI
  const generateKeywords = async (imageDataUrl, additionalInfo) => {
    if (!apiKey.trim()) {
      throw new Error('Необходимо указать API ключ OpenAI');
    }

    const prompts = {
      russian: `Проанализируй это изображение и создай:
1. Короткое описательное название на русском языке (максимум 5-7 слов)
2. Список ключевых слов на русском языке через запятую (15-25 слов)
3. Категории через запятую (например: природа, транспорт, люди, архитектура)
4. Editorial (yes/no) - подходит ли для редакционного использования
5. Mature content (yes/no) - есть ли взрослый контент
6. Illustration (yes/no) - является ли иллюстрацией

${additionalInfo ? `Дополнительная информация: ${additionalInfo}` : ''}

Формат ответа:
НАЗВАНИЕ: [название]
КЛЮЧЕВЫЕ СЛОВА: [слово1, слово2, слово3, ...]
КАТЕГОРИИ: [категория1, категория2, категория3]
EDITORIAL: [yes/no]
MATURE CONTENT: [yes/no]
ILLUSTRATION: [yes/no]

Ключевые слова должны описывать: объекты, цвета, настроение, стиль, композицию, освещение, время суток, сезон, действия, эмоции.`,
      
      english: `Analyze this image and create:
1. Short descriptive title in English (maximum 5-7 words)
2. List of keywords in English, separated by commas (15-25 words)
3. Categories separated by commas (e.g., nature, transportation, people, architecture)
4. Editorial (yes/no) - suitable for editorial use
5. Mature content (yes/no) - contains adult content
6. Illustration (yes/no) - is it an illustration

${additionalInfo ? `Additional information: ${additionalInfo}` : ''}

Response format:
TITLE: [title]
KEYWORDS: [keyword1, keyword2, keyword3, ...]
CATEGORIES: [category1, category2, category3]
EDITORIAL: [yes/no]
MATURE CONTENT: [yes/no]
ILLUSTRATION: [yes/no]

Keywords should describe: objects, colors, mood, style, composition, lighting, time of day, season, actions, emotions.`
    };

    const response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiKey}`
      },
      body: JSON.stringify({
        model: 'gpt-4o',
        messages: [
          {
            role: 'user',
            content: [
              { type: 'text', text: prompts[language] },
              { 
                type: 'image_url', 
                image_url: { url: imageDataUrl }
              }
            ]
          }
        ],
        max_tokens: 800
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(`OpenAI API Error: ${error.error?.message || 'Неизвестная ошибка'}`);
    }

    const data = await response.json();
    const content = data.choices[0].message.content;
    
    // Парсинг ответа
    const titleMatch = content.match(/(?:НАЗВАНИЕ|TITLE):\s*(.+)/i);
    const keywordsMatch = content.match(/(?:КЛЮЧЕВЫЕ СЛОВА|KEYWORDS):\s*(.+)/i);
    const categoriesMatch = content.match(/(?:КАТЕГОРИИ|CATEGORIES):\s*(.+)/i);
    const editorialMatch = content.match(/EDITORIAL:\s*(yes|no)/i);
    const matureMatch = content.match(/MATURE CONTENT:\s*(yes|no)/i);
    const illustrationMatch = content.match(/ILLUSTRATION:\s*(yes|no)/i);
    
    return {
      title: titleMatch ? titleMatch[1].trim() : 'Без названия',
      keywords: keywordsMatch ? keywordsMatch[1].trim() : 'ключевые слова не найдены',
      categories: categoriesMatch ? categoriesMatch[1].trim() : '',
      editorial: editorialMatch ? editorialMatch[1].toLowerCase() : 'no',
      matureContent: matureMatch ? matureMatch[1].toLowerCase() : 'no',
      illustration: illustrationMatch ? illustrationMatch[1].toLowerCase() : 'no'
    };
  };

  // Генерация для одного изображения (для повторов)
  const handleRetryImage = async (imageId) => {
    const image = images.find(img => img.id === imageId);
    if (!image) return;

    setRetryingImages(prev => new Set([...prev, imageId]));
    
    try {
      const result = await generateKeywords(image.dataUrl, image.additionalInfo);
      
      setImages(prev => prev.map(img => 
        img.id === imageId 
          ? { 
              ...img, 
              title: result.title, 
              keywords: result.keywords, 
              categories: result.categories,
              editorial: result.editorial,
              matureContent: result.matureContent,
              illustration: result.illustration,
              isProcessed: true 
            }
          : img
      ));
    } catch (error) {
      console.error('Ошибка повторной генерации для изображения:', error);
      setImages(prev => prev.map(img => 
        img.id === imageId 
          ? { ...img, title: 'Ошибка генерации', keywords: error.message, isProcessed: true }
          : img
      ));
    } finally {
      setRetryingImages(prev => {
        const newSet = new Set(prev);
        newSet.delete(imageId);
        return newSet;
      });
    }
  };
  const handleGenerateAll = async () => {
    if (!apiKey.trim()) {
      alert('Пожалуйста, введите API ключ OpenAI');
      return;
    }

    if (images.length === 0) {
      alert('Пожалуйста, загрузите изображения');
      return;
    }

    setIsGenerating(true);
    
    for (let i = 0; i < images.length; i++) {
      const image = images[i];
      
      try {
        const result = await generateKeywords(image.dataUrl, image.additionalInfo);
        
        setImages(prev => prev.map(img => 
          img.id === image.id 
            ? { 
                ...img, 
                title: result.title, 
                keywords: result.keywords, 
                categories: result.categories,
                editorial: result.editorial,
                matureContent: result.matureContent,
                illustration: result.illustration,
                isProcessed: true 
              }
            : img
        ));
      } catch (error) {
        console.error('Ошибка генерации для изображения:', error);
        setImages(prev => prev.map(img => 
          img.id === image.id 
            ? { ...img, title: 'Ошибка генерации', keywords: error.message, isProcessed: true }
            : img
        ));
      }
      
      // Небольшая задержка между запросами
      if (i < images.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }
    
    setIsGenerating(false);
  };

  // Копирование в буфер обмена
  const copyToClipboard = async (title, keywords) => {
    const text = `${title}\n${keywords}`;
    try {
      await navigator.clipboard.writeText(text);
      alert(language === 'russian' ? 'Скопировано в буфер обмена!' : 'Copied to clipboard!');
    } catch (err) {
      console.error('Ошибка копирования:', err);
      alert(language === 'russian' ? 'Ошибка копирования в буфер обмена' : 'Failed to copy to clipboard');
    }
  };

  // Экспорт в CSV
  const exportToCSV = () => {
    const processedImages = images.filter(img => img.isProcessed);
    if (processedImages.length === 0) {
      alert(language === 'russian' ? 'Нет обработанных изображений для экспорта' : 'No processed images to export');
      return;
    }

    const csvHeader = 'Filename,Description,Keywords,Categories,Editorial,Mature content,Illustration\n';
    const csvRows = processedImages.map(img => {
      const filename = img.originalName;
      const description = `"${img.title.replace(/"/g, '""')}"`;
      const keywords = `"${img.keywords.replace(/"/g, '""')}"`;
      const categories = `"${img.categories.replace(/"/g, '""')}"`;
      const editorial = img.editorial;
      const matureContent = img.matureContent;
      const illustration = img.illustration;
      
      return `${filename},${description},${keywords},${categories},${editorial},${matureContent},${illustration}`;
    }).join('\n');

    const csvContent = csvHeader + csvRows;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'keywords_export.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Копирование CSV в буфер обмена
  const copyCSVToClipboard = async () => {
    const processedImages = images.filter(img => img.isProcessed);
    if (processedImages.length === 0) {
      alert(language === 'russian' ? 'Нет обработанных изображений для копирования' : 'No processed images to copy');
      return;
    }

    const csvHeader = 'Filename,Description,Keywords,Categories,Editorial,Mature content,Illustration\n';
    const csvRows = processedImages.map(img => {
      const filename = img.originalName;
      const description = `"${img.title.replace(/"/g, '""')}"`;
      const keywords = `"${img.keywords.replace(/"/g, '""')}"`;
      const categories = `"${img.categories.replace(/"/g, '""')}"`;
      const editorial = img.editorial;
      const matureContent = img.matureContent;
      const illustration = img.illustration;
      
      return `${filename},${description},${keywords},${categories},${editorial},${matureContent},${illustration}`;
    }).join('\n');

    const csvContent = csvHeader + csvRows;
    
    try {
      await navigator.clipboard.writeText(csvContent);
      alert(language === 'russian' ? 'CSV данные скопированы в буфер обмена!' : 'CSV data copied to clipboard!');
    } catch (err) {
      console.error('Ошибка копирования:', err);
      alert(language === 'russian' ? 'Ошибка копирования в буфер обмена' : 'Failed to copy to clipboard');
    }
  };

  return (
    <div className={`min-h-screen transition-all duration-300 ${
      isDarkMode 
        ? 'bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900' 
        : 'bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50'
    }`}>
      <div className="p-4 max-w-7xl mx-auto">
        {/* Header with Theme Toggle */}
        <div className={`${
          isDarkMode ? 'bg-gray-800/70' : 'bg-white/70'
        } backdrop-blur-xl rounded-2xl shadow-2xl border ${
          isDarkMode ? 'border-gray-700' : 'border-white/50'
        } p-6 mb-8 transition-all duration-300`}>
          <div className="flex justify-between items-center mb-6">
            <h1 className={`text-4xl font-bold bg-gradient-to-r ${
              isDarkMode 
                ? 'from-blue-400 to-purple-400' 
                : 'from-blue-600 to-purple-600'
            } bg-clip-text text-transparent`}>
              {t.title}
            </h1>
            
            <div className="flex items-center gap-4">
              {/* Language Toggle */}
              <div className={`relative inline-flex items-center p-1 rounded-xl border-2 transition-all duration-300 ${
                isDarkMode 
                  ? 'bg-gray-700/50 border-gray-600' 
                  : 'bg-white/80 border-gray-200'
              }`}>
                {/* Background Slider */}
                <div 
                  className={`absolute inset-y-1 w-16 rounded-lg transition-all duration-300 ease-out transform ${
                    language === 'russian' ? 'translate-x-0' : 'translate-x-16'
                  } ${
                    isDarkMode 
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 shadow-lg' 
                      : 'bg-gradient-to-r from-blue-500 to-purple-600 shadow-lg'
                  }`}
                />
                
                {/* Russian Option */}
                <button
                  onClick={() => setLanguage('russian')}
                  className={`relative z-10 flex items-center justify-center w-16 py-2 rounded-lg font-semibold text-xs transition-all duration-300 ${
                    language === 'russian'
                      ? 'text-white transform scale-105'
                      : isDarkMode 
                        ? 'text-gray-300 hover:text-white' 
                        : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  RU
                </button>
                
                {/* English Option */}
                <button
                  onClick={() => setLanguage('english')}
                  className={`relative z-10 flex items-center justify-center w-16 py-2 rounded-lg font-semibold text-xs transition-all duration-300 ${
                    language === 'english'
                      ? 'text-white transform scale-105'
                      : isDarkMode 
                        ? 'text-gray-300 hover:text-white' 
                        : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  EN
                </button>
              </div>

              {/* Theme Toggle */}
              <button
                onClick={() => setIsDarkMode(!isDarkMode)}
                className={`p-3 rounded-full transition-all duration-300 hover:scale-110 ${
                  isDarkMode 
                    ? 'bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30' 
                    : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
                }`}
              >
                {isDarkMode ? <Sun className="w-6 h-6" /> : <Moon className="w-6 h-6" />}
              </button>
            </div>
          </div>
          
          <div className="mb-6">
            {/* API Key Input */}
            <div>
              <label className={`block text-sm font-semibold mb-3 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                {t.apiKeyLabel}
              </label>
              <input
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder={t.apiKeyPlaceholder}
                className={`w-full px-4 py-3 rounded-xl border-2 transition-all duration-300 ${
                  isDarkMode 
                    ? 'bg-gray-700/50 border-gray-600 text-white placeholder-gray-400 focus:border-blue-500 focus:bg-gray-700/70' 
                    : 'bg-white/80 border-gray-200 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:bg-white'
                } focus:ring-4 focus:ring-blue-500/20 focus:outline-none`}
              />
            </div>
          </div>
        </div>

        {/* File Upload Area */}
        <div className={`${
          isDarkMode ? 'bg-gray-800/70' : 'bg-white/70'
        } backdrop-blur-xl rounded-2xl shadow-2xl border ${
          isDarkMode ? 'border-gray-700' : 'border-white/50'
        } p-8 mb-8 transition-all duration-300`}>
          
          {/* Processing Progress */}
          {isProcessingImages && (
            <div className="mb-8">
              <div className="flex items-center justify-between mb-3">
                <div className={`text-lg font-semibold ${
                  isDarkMode ? 'text-white' : 'text-gray-800'
                }`}>
                  {t.processingImages}
                </div>
                <div className={`text-sm font-medium ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-600'
                }`}>
                  {processingProgress.current} / {processingProgress.total}
                </div>
              </div>
              
              {/* Progress Bar */}
              <div className={`w-full rounded-full h-3 ${
                isDarkMode ? 'bg-gray-700' : 'bg-gray-200'
              }`}>
                <div 
                  className="h-3 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 transition-all duration-300 ease-out"
                  style={{ 
                    width: `${processingProgress.total > 0 ? (processingProgress.current / processingProgress.total) * 100 : 0}%` 
                  }}
                />
              </div>
              
              <div className={`text-xs mt-2 text-center ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>
                {language === 'russian' 
                  ? 'Сжимаем и оптимизируем изображения для лучшей производительности...'
                  : 'Compressing and optimizing images for better performance...'
                }
              </div>
            </div>
          )}

          <div
            className={`border-2 border-dashed rounded-2xl p-12 text-center transition-all duration-300 transform ${
              dragActive 
                ? isDarkMode 
                  ? 'border-blue-400 bg-blue-500/10 scale-105' 
                  : 'border-blue-500 bg-blue-50 scale-105'
                : isDarkMode
                  ? 'border-gray-600 hover:border-gray-500 hover:bg-gray-700/30'
                  : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
            } ${isProcessingImages ? 'opacity-50 pointer-events-none' : ''}`}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            <div className={`inline-flex p-4 rounded-full mb-6 ${
              isDarkMode ? 'bg-gradient-to-br from-blue-500/20 to-purple-500/20' : 'bg-gradient-to-br from-blue-100 to-purple-100'
            }`}>
              {isProcessingImages ? (
                <Loader2 className={`w-12 h-12 animate-spin ${
                  isDarkMode ? 'text-blue-400' : 'text-blue-600'
                }`} />
              ) : (
                <Upload className={`w-12 h-12 ${
                  isDarkMode ? 'text-blue-400' : 'text-blue-600'
                }`} />
              )}
            </div>
            <div className={`text-2xl font-bold mb-3 ${
              isDarkMode ? 'text-white' : 'text-gray-800'
            }`}>
              {isProcessingImages ? t.dropImagesProcessing : t.dropImages}
            </div>
            <div className={`text-lg mb-6 ${
              isDarkMode ? 'text-gray-400' : 'text-gray-600'
            }`}>
              {isProcessingImages ? t.waitOptimizing : t.clickToSelect}
            </div>
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={handleFileInput}
              className="hidden"
              id="file-input"
              disabled={isProcessingImages}
            />
            <label
              htmlFor="file-input"
              className={`inline-flex items-center px-8 py-4 bg-gradient-to-r text-white font-semibold rounded-xl transition-all duration-300 transform hover:scale-105 hover:shadow-xl ${
                isProcessingImages
                  ? 'opacity-50 cursor-not-allowed'
                  : isDarkMode 
                    ? 'from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 cursor-pointer' 
                    : 'from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 cursor-pointer'
              }`}
            >
              <Upload className="w-5 h-5 mr-3" />
              {isProcessingImages ? t.processing : t.selectImages}
            </label>
          </div>

          {/* Generate Button */}
          {images.length > 0 && !isProcessingImages && (
            <div className="text-center mt-8">
              <button
                onClick={handleGenerateAll}
                disabled={isGenerating || !apiKey.trim()}
                className={`inline-flex items-center px-10 py-4 bg-gradient-to-r text-white font-bold text-lg rounded-xl transition-all duration-300 transform hover:scale-105 disabled:scale-100 disabled:opacity-50 disabled:cursor-not-allowed ${
                  isDarkMode
                    ? 'from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 shadow-green-500/25'
                    : 'from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 shadow-green-500/25'
                } hover:shadow-2xl`}
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="animate-spin w-6 h-6 mr-3" />
                    {t.generatingMagic}
                  </>
                ) : (
                  <>
                    {t.generateKeywords}
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        {/* Images Grid */}
        {images.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8 mb-8">
            {images.map((image) => (
              <div key={image.id} className={`${
                isDarkMode ? 'bg-gray-800/70' : 'bg-white/70'
              } backdrop-blur-xl rounded-2xl shadow-2xl border ${
                isDarkMode ? 'border-gray-700' : 'border-white/50'
              } overflow-hidden transition-all duration-300 hover:scale-105 hover:shadow-3xl group`}>
                <div className="relative overflow-hidden">
                  <img
                    src={image.dataUrl}
                    alt={image.originalName}
                    className="w-full h-56 object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                  <button
                    onClick={() => removeImage(image.id)}
                    className="absolute top-3 right-3 p-2 bg-red-500/80 text-white rounded-full hover:bg-red-500 transition-all duration-300 transform hover:scale-110 backdrop-blur-sm"
                  >
                    <X className="w-4 h-4" />
                  </button>
                  <div className="absolute bottom-3 left-3 right-3">
                    <div className={`text-sm font-medium truncate ${
                      isDarkMode ? 'text-white' : 'text-white'
                    }`}>
                      {image.originalName}
                    </div>
                  </div>
                </div>
                
                <div className="p-6">
                  {/* Additional Info Input */}
                  <div className="mb-6">
                    <label className={`block text-xs font-semibold mb-2 ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      {t.additionalContext}
                    </label>
                    <input
                      type="text"
                      value={image.additionalInfo}
                      onChange={(e) => updateAdditionalInfo(image.id, e.target.value)}
                      placeholder={t.contextPlaceholder}
                      className={`w-full px-4 py-3 text-sm rounded-xl border-2 transition-all duration-300 ${
                        isDarkMode 
                          ? 'bg-gray-700/50 border-gray-600 text-white placeholder-gray-400 focus:border-blue-500' 
                          : 'bg-white/80 border-gray-200 text-gray-900 placeholder-gray-400 focus:border-blue-500'
                      } focus:ring-4 focus:ring-blue-500/20 focus:outline-none`}
                    />
                  </div>

                  {/* Generated Results */}
                  {image.isProcessed && (
                    <div className={`border-t-2 pt-6 space-y-4 ${
                      isDarkMode ? 'border-gray-700' : 'border-gray-200'
                    }`}>
                      <div>
                        <label className={`block text-xs font-semibold mb-2 ${
                          isDarkMode ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                          {t.title_field}
                        </label>
                        <div className={`text-sm font-medium p-3 rounded-xl ${
                          isDarkMode ? 'bg-gray-700/50 text-white' : 'bg-gray-50 text-gray-800'
                        }`}>
                          {image.title}
                        </div>
                      </div>
                      
                      <div>
                        <label className={`block text-xs font-semibold mb-2 ${
                          isDarkMode ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                          {t.keywords_field}
                        </label>
                        <div className={`text-sm p-3 rounded-xl max-h-24 overflow-y-auto ${
                          isDarkMode ? 'bg-gray-700/50 text-gray-300' : 'bg-gray-50 text-gray-700'
                        }`}>
                          {image.keywords}
                        </div>
                      </div>

                      <div>
                        <label className={`block text-xs font-semibold mb-2 ${
                          isDarkMode ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                          {t.categories_field}
                        </label>
                        <div className={`text-sm p-3 rounded-xl ${
                          isDarkMode ? 'bg-gray-700/50 text-gray-300' : 'bg-gray-50 text-gray-700'
                        }`}>
                          {image.categories}
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-3 text-xs">
                        <div className="text-center">
                          <div className={`text-gray-500 mb-1 ${isDarkMode ? 'text-gray-400' : ''}`}>{t.editorial}</div>
                          <span className={`px-3 py-1 font-medium rounded-full ${
                            image.editorial === 'yes' 
                              ? 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-400' 
                              : 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-400'
                          }`}>
                            {image.editorial}
                          </span>
                        </div>
                        <div className="text-center">
                          <div className={`text-gray-500 mb-1 ${isDarkMode ? 'text-gray-400' : ''}`}>{t.mature}</div>
                          <span className={`px-3 py-1 font-medium rounded-full ${
                            image.matureContent === 'yes' 
                              ? 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-400' 
                              : 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-400'
                          }`}>
                            {image.matureContent}
                          </span>
                        </div>
                        <div className="text-center">
                          <div className={`text-gray-500 mb-1 ${isDarkMode ? 'text-gray-400' : ''}`}>{t.illustration}</div>
                          <span className={`px-3 py-1 font-medium rounded-full ${
                            image.illustration === 'yes' 
                              ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-400' 
                              : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                          }`}>
                            {image.illustration}
                          </span>
                        </div>
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="flex gap-2">
                        <button
                          onClick={() => copyToClipboard(image.title, image.keywords)}
                          className={`flex-1 flex items-center justify-center px-4 py-3 bg-gradient-to-r ${
                            isDarkMode 
                              ? 'from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500' 
                              : 'from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700'
                          } text-white font-semibold rounded-xl transition-all duration-300 transform hover:scale-105`}
                        >
                          <Copy className="w-4 h-4 mr-2" />
                          {t.copyData}
                        </button>
                        
                        {/* Retry Button - show for failed generations */}
                        {(image.title.includes('Ошибка') || image.title.includes('Error') || 
                          image.keywords.includes('не найдены') || image.keywords.includes('not found')) && (
                          <button
                            onClick={() => handleRetryImage(image.id)}
                            disabled={retryingImages.has(image.id)}
                            className={`px-4 py-3 bg-gradient-to-r ${
                              isDarkMode 
                                ? 'from-orange-600 to-red-600 hover:from-orange-500 hover:to-red-500' 
                                : 'from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600'
                            } text-white font-semibold rounded-xl transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:scale-100`}
                          >
                            {retryingImages.has(image.id) ? (
                              <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                              </>
                            ) : (
                              <>
                                {t.retry}
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {(images.length === 0 && !isProcessingImages) && (
          <div className="text-center py-20">
            <div className={`inline-flex p-6 rounded-full mb-6 ${
              isDarkMode ? 'bg-gradient-to-br from-gray-700/50 to-gray-800/50' : 'bg-gradient-to-br from-gray-100 to-gray-200'
            }`}>
              <Upload className={`w-16 h-16 ${
                isDarkMode ? 'text-gray-400' : 'text-gray-400'
              }`} />
            </div>
            <h3 className={`text-2xl font-bold mb-2 ${
              isDarkMode ? 'text-white' : 'text-gray-800'
            }`}>
              {t.readyToGenerate}
            </h3>
            <p className={`text-lg mb-4 ${
              isDarkMode ? 'text-gray-400' : 'text-gray-600'
            }`}>
              {t.uploadToStart}
            </p>
            <div className={`text-sm ${
              isDarkMode ? 'text-gray-500' : 'text-gray-500'
            }`}>
              {t.features}
            </div>
          </div>
        )}

        {/* Processing Status */}
        {isProcessingImages && images.length === 0 && (
          <div className="text-center py-20">
            <div className={`inline-flex p-6 rounded-full mb-6 ${
              isDarkMode ? 'bg-gradient-to-br from-blue-500/20 to-purple-500/20' : 'bg-gradient-to-br from-blue-100 to-purple-100'
            }`}>
              <Loader2 className={`w-16 h-16 animate-spin ${
                isDarkMode ? 'text-blue-400' : 'text-blue-600'
              }`} />
            </div>
            <h3 className={`text-2xl font-bold mb-2 ${
              isDarkMode ? 'text-white' : 'text-gray-800'
            }`}>
              {t.processingYourImages}
            </h3>
            <p className={`text-lg mb-4 ${
              isDarkMode ? 'text-gray-400' : 'text-gray-600'
            }`}>
              {t.compressingOptimizing} {processingProgress.total} {t.images}
            </p>
            <div className={`text-sm ${
              isDarkMode ? 'text-gray-500' : 'text-gray-500'
            }`}>
              {t.usingParallelProcessing}
            </div>
          </div>
        )}

        {/* Results Table */}
        {images.filter(img => img.isProcessed).length > 0 && (
          <div className={`${
            isDarkMode ? 'bg-gray-800/70' : 'bg-white/70'
          } backdrop-blur-xl rounded-2xl shadow-2xl border ${
            isDarkMode ? 'border-gray-700' : 'border-white/50'
          } p-8 transition-all duration-300`}>
            <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-8 gap-4">
              <h2 className={`text-3xl font-bold bg-gradient-to-r ${
                isDarkMode 
                  ? 'from-green-400 to-blue-400' 
                  : 'from-green-600 to-blue-600'
              } bg-clip-text text-transparent`}>
                {t.processingResults}
              </h2>
              <div className="flex gap-3">
                <button
                  onClick={copyCSVToClipboard}
                  className={`flex items-center px-6 py-3 bg-gradient-to-r ${
                    isDarkMode 
                      ? 'from-gray-600 to-gray-700 hover:from-gray-500 hover:to-gray-600' 
                      : 'from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700'
                  } text-white font-semibold rounded-xl transition-all duration-300 transform hover:scale-105`}
                >
                  <Copy className="w-4 h-4 mr-2" />
                  {t.copyCSV}
                </button>
                <button
                  onClick={exportToCSV}
                  className={`flex items-center px-6 py-3 bg-gradient-to-r ${
                    isDarkMode 
                      ? 'from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500' 
                      : 'from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600'
                  } text-white font-semibold rounded-xl transition-all duration-300 transform hover:scale-105`}
                >
                  <Download className="w-4 h-4 mr-2" />
                  {t.downloadCSV}
                </button>
              </div>
            </div>
            
            <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
              <table className="w-full text-sm">
                <thead className={`${
                  isDarkMode ? 'bg-gray-700/50' : 'bg-gray-50'
                }`}>
                  <tr>
                    <th className={`px-6 py-4 text-left font-semibold ${
                      isDarkMode ? 'text-gray-200' : 'text-gray-900'
                    }`}>{t.filename}</th>
                    <th className={`px-6 py-4 text-left font-semibold ${
                      isDarkMode ? 'text-gray-200' : 'text-gray-900'
                    }`}>{t.description}</th>
                    <th className={`px-6 py-4 text-left font-semibold ${
                      isDarkMode ? 'text-gray-200' : 'text-gray-900'
                    }`}>{t.keywords}</th>
                    <th className={`px-6 py-4 text-left font-semibold ${
                      isDarkMode ? 'text-gray-200' : 'text-gray-900'
                    }`}>{t.categories}</th>
                    <th className={`px-6 py-4 text-left font-semibold ${
                      isDarkMode ? 'text-gray-200' : 'text-gray-900'
                    }`}>{t.editorial}</th>
                    <th className={`px-6 py-4 text-left font-semibold ${
                      isDarkMode ? 'text-gray-200' : 'text-gray-900'
                    }`}>{t.mature}</th>
                    <th className={`px-6 py-4 text-left font-semibold ${
                      isDarkMode ? 'text-gray-200' : 'text-gray-900'
                    }`}>{t.illustration}</th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${
                  isDarkMode ? 'divide-gray-700' : 'divide-gray-200'
                }`}>
                  {images.filter(img => img.isProcessed).map((image) => (
                    <tr key={image.id} className={`transition-colors duration-200 ${
                      isDarkMode ? 'hover:bg-gray-700/30' : 'hover:bg-gray-50'
                    }`}>
                      <td className={`px-6 py-4 font-medium max-w-40 truncate ${
                        isDarkMode ? 'text-white' : 'text-gray-900'
                      }`}>
                        {image.originalName}
                      </td>
                      <td className={`px-6 py-4 max-w-48 ${
                        isDarkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        <div className="max-h-20 overflow-y-auto">
                          {image.title}
                        </div>
                      </td>
                      <td className={`px-6 py-4 max-w-64 ${
                        isDarkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        <div className="max-h-20 overflow-y-auto">
                          {image.keywords}
                        </div>
                      </td>
                      <td className={`px-6 py-4 max-w-32 ${
                        isDarkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        <div className="max-h-20 overflow-y-auto">
                          {image.categories}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                          image.editorial === 'yes' 
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-400' 
                            : 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-400'
                        }`}>
                          {image.editorial}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                          image.matureContent === 'yes' 
                            ? 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-400' 
                            : 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-400'
                        }`}>
                          {image.matureContent}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                          image.illustration === 'yes' 
                            ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-400' 
                            : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                        }`}>
                          {image.illustration}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <div className={`mt-6 flex items-center justify-between text-sm ${
              isDarkMode ? 'text-gray-400' : 'text-gray-600'
            }`}>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <span>{t.processed} {images.filter(img => img.isProcessed).length} {t.of} {images.length} {t.images}</span>
              </div>
              <div className="text-xs opacity-75">
                {t.aiPoweredGeneration}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default KeywordGenerator;